import { flatten, values } from "ramda";

import {
  lockRoute,
  setStops,
  setRequests,
  setTransferRouteId,
  setTransferFromRouteId,
  setTransferredStops,
} from "../../route";

import { showNotification } from "../../notifications";
import { NotificationType } from "../../../../features/Notifier";
import { DISMISS } from "../../../../constants/strings";
import {
  getSelectedStops,
  getSelectedParcels,
} from "../../../selectors/ui/routeDetail";
import { isCollectionStop } from "../../../../models/stop";
import {
  generateCollectionStopTransferRequests,
  processParcelTransferToRequests,
  getValidateParcelList,
} from "../../../../models/request";
import {
  getRouteDetail,
  getRouteStops,
  getRouteRequests,
  getRequestOrder,
  getRouteTransferredStops,
} from "../../../selectors/route";
import {
  removeSelectedStops,
  updateSeqAndDisplayNumbers,
} from "../../../../models/moveStops";
import { isRouteStatusLoading } from "../../../../models/routeDetail";
import { clearSelection } from "./stopFilters";
import { updateStopInStopList } from "../../../../models/route";
import { validateStopMerge } from "../../stop";
import { getErrorMessage } from "../../../../utils/errors";

export const processStopTransfer = (
  transferRouteId,
  currentRoute,
  processedData = {}
) => async (dispatch, getState) => {
  const state = getState();
  const { stop, routeStops, transferredRouteStops } = processedData;
  const selectedStops = stop ? [stop] : getSelectedStops(state);
  const stops = routeStops || getRouteStops(state);
  const transferredStops =
    transferredRouteStops || getRouteTransferredStops(state);
  const requestOrder = getRequestOrder(state);
  let requests = [];
  selectedStops.forEach((stop) => {
    if (isCollectionStop(stop)) {
      requests.push(
        generateCollectionStopTransferRequests(
          currentRoute.routeId,
          transferRouteId,
          stop,
          requestOrder
        )
      );
    } else {
      requests.push(
        processParcelTransferToRequests(
          currentRoute,
          transferRouteId,
          stop,
          stop.parcel,
          requestOrder
        )
      );
    }
  });

  const updatedStops = removeSelectedStops(stops, selectedStops);
  const updatedTransferredStops = [...transferredStops, ...selectedStops];

  return {
    updatedStops,
    requests: flatten(requests),
    transferredStop: updatedTransferredStops,
  };
};

export const processParcelTransfer = (
  transferRouteId,
  currentRoute,
  processedData = {}
) => async (dispatch, getState) => {
  const state = getState();
  const { stop, parcels, routeStops, transferredRouteStops } = processedData;
  const stops = routeStops || getRouteStops(state);
  const selectedStop = stop ? [stop] : getSelectedStops(state);
  const selectedParcels = parcels || values(getSelectedParcels(state));
  const transferredStops =
    transferredRouteStops || getRouteTransferredStops(state);
  const requestOrder = getRequestOrder(state);
  const requests = processParcelTransferToRequests(
    currentRoute,
    transferRouteId,
    selectedStop[0],
    selectedParcels,
    requestOrder
  );

  const toValidateParcelList = getValidateParcelList(
    selectedStop[0],
    selectedParcels
  );
  if (toValidateParcelList.length > 0) {
    const updatedStop = await dispatch(
      validateStopParcels(toValidateParcelList)
    );

    return updatedStop
      ? {
          requests,
          updatedStops: updateStopInStopList(stops, {
            ...selectedStop[0],
            ...updatedStop,
          }),
          transferredStop: transferredStops,
        }
      : {};
  }
  return {
    requests,
    updatedStops: removeSelectedStops(stops, selectedStop),
    transferredStop: [...transferredStops, ...selectedStop],
  };
};

export const updateTransferEntities = ({
  updatedStops,
  requests,
  transferredStop,
  transferRouteId,
}) => (dispatch, getState) => {
  const state = getState();
  const currentRoute = getRouteDetail(state);
  const routeRequests = getRouteRequests(state);

  if (requests) {
    const updatedStopList = updateSeqAndDisplayNumbers(
      updatedStops,
      !isRouteStatusLoading(currentRoute)
    );
    dispatch(clearSelection());
    dispatch(setStops(updatedStopList));
    transferredStop && dispatch(setTransferredStops(transferredStop));
    dispatch(setRequests(routeRequests.concat(requests)));
    dispatch(setTransferRouteId(transferRouteId));
    dispatch(setTransferFromRouteId(currentRoute.routeId));
    return requests;
  }
  return false;
};

export const processTransfer = (
  isStopsSelected,
  transferRouteId,
  processedData = {}
) => async (dispatch, getState) => {
  const currentUserLock = await dispatch(lockRoute());

  if (!currentUserLock) {
    return false;
  }
  const state = getState();
  const currentRoute = getRouteDetail(state);

  const processFunction = isStopsSelected
    ? processStopTransfer
    : processParcelTransfer;
  const { updatedStops, requests, transferredStop } = await dispatch(
    processFunction(transferRouteId, currentRoute, processedData)
  );
  const updated = await dispatch(
    updateTransferEntities({
      updatedStops,
      requests,
      transferredStop,
      transferRouteId,
    })
  );
  return updated;
};

export const validateStopParcels = (toValidateParcelList) => async (
  dispatch
) => {
  try {
    const updatedStop = await dispatch(
      validateStopMerge({
        parcels: toValidateParcelList,
        checkOpenRequests: true,
        splitStop: true,
      })
    );
    return updatedStop;
  } catch (error) {
    dispatch(
      showNotification({
        type: NotificationType.ACTION,
        message: getErrorMessage(error),
        buttonText: DISMISS,
      })
    );
    return null;
  }
};
